import { useEffect } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import type { RootState } from "@/state/store";
import { adoptRevision, fetchWorkspace } from "@/state/slices/workspacesSlice";
import { isWorkspaceDirty } from "@/hooks/useWorkspaceAutoSave";
import { apiUrl } from "@/api/client";
import { getClientId } from "@/utils/clientId";

/**
 * Workspace live sync — keeps every session on the same workspace in
 * lockstep without waiting for the next save attempt's OCC 409.
 *
 * Subscribes an EventSource to `/api/workspaces/{name}/stream`. The
 * server pokes `workspace_updated` (revision + the saver's client_id)
 * after every successful save; a `workspace_sync` event on (re)connect
 * carries the current revision so saves missed while offline are caught.
 *
 * On a relevant poke we simply dispatch `fetchWorkspace(name)` — the
 * exact thunk the boot/409 paths use — so hydrate broadcasting,
 * revision bookkeeping, and the autosave baseline reset are all reused
 * untouched.
 *
 * Skip rules:
 *  - own echo: event.client_id === our sessionStorage id (the save we
 *    just made bouncing back);
 *  - stale/duplicate: event revision <= the revision we already know
 *    (monotonic guard, also true right after our own save);
 *  - already hydrating: a fetch is in flight; it will land the newest
 *    state anyway.
 */
export function useWorkspaceLiveSync(): void {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const currentName = useAppSelector((s) => s.workspaces.currentName);
  const meStatus = useAppSelector((s) => s.me.status);

  useEffect(() => {
    if (!currentName || meStatus !== "succeeded") return;
    const name = currentName;
    const url = apiUrl(`/api/workspaces/${encodeURIComponent(name)}/stream`);
    // withCredentials so the session cookie rides on the cross-origin SSE.
    const es = new EventSource(url, { withCredentials: true });

    const maybeFetch = (revision: number, clientId: string | null) => {
      if (clientId && clientId === getClientId()) return; // own echo
      const state = store.getState();
      const ws = state.workspaces;
      if (ws.currentName !== name) return; // switched away mid-flight
      if (ws.isHydrating) return; // a fetch is already landing
      const known = ws.revisionByName[name] ?? 0;
      if (revision <= known) return; // stale or already seen
      // A remote save landed while THIS session has unsaved local
      // edits (e.g. a timeframe change still inside the autosave
      // debounce). Hydrating now would clobber the newer local intent
      // — the "my timeframe snapped back" bug. Adopt the broadcast
      // revision instead so our pending autosave lands cleanly on top:
      // for one user across their own sessions, most-recent-action-
      // wins is the right semantics.
      if (ws.saving || isWorkspaceDirty(state)) {
        dispatch(adoptRevision({ name, revision }));
        return;
      }
      void dispatch(fetchWorkspace(name));
    };

    const onUpdated = (ev: MessageEvent) => {
      try {
        const d = JSON.parse(ev.data);
        maybeFetch(Number(d.revision ?? 0), d.client_id ?? null);
      } catch {
        // ignore malformed
      }
    };
    const onSync = (ev: MessageEvent) => {
      try {
        const d = JSON.parse(ev.data);
        // No client_id on the connect probe — it's the server's current
        // revision, not a save event.
        maybeFetch(Number(d.revision ?? 0), null);
      } catch {
        // ignore malformed
      }
    };

    es.addEventListener("workspace_updated", onUpdated as EventListener);
    es.addEventListener("workspace_sync", onSync as EventListener);

    return () => {
      es.removeEventListener("workspace_updated", onUpdated as EventListener);
      es.removeEventListener("workspace_sync", onSync as EventListener);
      es.close();
    };
  }, [dispatch, store, currentName, meStatus]);
}
